import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../styles/dashboardTheme.css";
import "../../../styles/managementTheme.css";
import { showToast } from "../../../utils/showToast";
import { usePermissions } from "../../../utils/permissions";

import "./styles/roles.css";

const tipoOptions = [
  { value: "estrategico", label: "Estrategico" },
  { value: "administrativo", label: "Administrativo" },
  { value: "operativo", label: "Operativo" },
  { value: "academico", label: "Academico" },
  { value: "financiero", label: "Financiero" },
  { value: "externo", label: "Externo" },
];

const moduloOrder = ["General", "Seguridad", "Operativo", "Academico", "Financiero", "RRHH"];

const filterGroupedPermisos = (grouped = {}, term = "") => {
  const query = term.trim().toLowerCase();
  if (!query) return grouped;

  const filtered = {};
  Object.entries(grouped).forEach(([modulo, submodulos]) => {
    Object.entries(submodulos || {}).forEach(([submodulo, permisos]) => {
      const matches = (permisos || []).filter((permiso) => {
        const target = `${permiso.descripcion || ""} ${permiso.codigo || ""} ${permiso.ruta || ""}`.toLowerCase();
        return (
          target.includes(query) ||
          modulo.toLowerCase().includes(query) ||
          submodulo.toLowerCase().includes(query)
        );
      });
      if (matches.length) {
        if (!filtered[modulo]) filtered[modulo] = {};
        filtered[modulo][submodulo] = matches;
      }
    });
  });
  return filtered;
};

const countPermisos = (grouped = {}) =>
  Object.values(grouped).reduce(
    (total, submods) =>
      total +
      Object.values(submods || {}).reduce(
        (subtotal, lista) => subtotal + (Array.isArray(lista) ? lista.length : 0),
        0
      ),
    0
  );

const orderModules = (modules = []) => {
  const remaining = [...modules];
  const ordered = moduloOrder.filter((name) => remaining.includes(name));
  return [...ordered, ...remaining.filter((name) => !ordered.includes(name))];
};

export default function RolesView({ data, state, form, alerts, selectors, actions }) {
  const { roles, permisos, groupedPermisos, selectedRole } = data;
  const {
    loading,
    permisosLoading,
    listError,
    permisosError,
    saving,
    // deleting,
    isNuevo,
    hasChanges,
  } = state;
  const { values: formValues, setValues: setFormValues } = form;
  const { alerta, dismissAlert } = alerts;
  const {
    selectRole,
    startNewRole,
    resetForm,
    togglePermiso,
    toggleGroup,
    handleSubmit,
    // handleDelete,
    reloadRoles,
    reloadPermisos,
  } = actions;

  const [roleSearch, setRoleSearch] = useState("");
  const [permSearch, setPermSearch] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [activeModule, setActiveModule] = useState("");
  // const [confirmDelete, setConfirmDelete] = useState(false);
  const { hasPermission } = usePermissions();
  const canManageRoles = hasPermission("roles.gestion");
  const canCreateRoles = canManageRoles;
  const canEditRoles = canManageRoles;

  useEffect(() => {
    if (alerta?.message) {
      showToast({
        type: alerta.type || "info",
        message: alerta.message,
        onClose: dismissAlert,
      });
    }
  }, [alerta, dismissAlert]);

  const filteredRoles = useMemo(() => {
    const term = roleSearch.trim().toLowerCase();
    if (!term) return roles;
    return roles.filter((rol) =>
      `${rol.nombre || ""} ${rol.descripcion || ""}`.toLowerCase().includes(term)
    );
  }, [roles, roleSearch]);

  const visiblePermisos = useMemo(
    () => filterGroupedPermisos(groupedPermisos, permSearch),
    [groupedPermisos, permSearch]
  );

  const modules = useMemo(
    () => orderModules(Object.keys(visiblePermisos)),
    [visiblePermisos]
  );

  const modulesWithCount = useMemo(
    () =>
      modules.map((modulo) => {
        const submods = visiblePermisos[modulo] || {};
        const total = Object.values(submods || {}).reduce(
          (acc, lista) => acc + (Array.isArray(lista) ? lista.length : 0),
          0
        );
        return { modulo, total };
      }),
    [modules, visiblePermisos]
  );

  useEffect(() => {
    if (!modulesWithCount.length) {
      setActiveModule("");
      return;
    }
    const moduleNames = modulesWithCount.map((m) => m.modulo);
    if (!activeModule || !moduleNames.includes(activeModule)) {
      setActiveModule(moduleNames[0]);
    }
  }, [modulesWithCount, activeModule]);

  const selectedPermisos = formValues?.permisos || [];
  const totalPermisos = permisos.length;
  const visiblePermisosCount = countPermisos(visiblePermisos);

  const handleOpenRole = (roleId) => {
    if (!canEditRoles) return;
    if (roleId) selectRole(roleId);
    setShowEditor(true);
  };

  const handleNewRole = () => {
    if (!canCreateRoles) return;
    startNewRole();
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setPermSearch("");
    resetForm();
  };

  if (showEditor) {
    return (
      <section className="dashboard-page management-page management-theme-cyan container-fluid py-4">
        <RoleEditorPage
          isNuevo={isNuevo}
          modules={modulesWithCount}
          activeModule={activeModule}
          setActiveModule={setActiveModule}
          formValues={formValues}
          setFormValues={setFormValues}
          selectedRole={selectedRole}
          selectedPermisos={selectedPermisos}
          visiblePermisos={visiblePermisos}
          permisosLoading={permisosLoading}
          hasChanges={hasChanges}
          saving={saving}
          permSearch={permSearch}
          setPermSearch={setPermSearch}
          totalPermisos={totalPermisos}
          visiblePermisosCount={visiblePermisosCount}
          onTogglePermiso={togglePermiso}
          onToggleGroup={toggleGroup}
          onSubmit={handleSubmit}
          onReset={resetForm}
          onBack={handleCloseEditor}
          canManageRoles={canManageRoles}
        />
      </section>
    );
  }

  return (
    <section className="dashboard-page management-page management-theme-cyan container-fluid py-4">
      <div className="management-card management-header mb-4">
        <div>
          <p className="dashboard-eyebrow mb-1 text-uppercase">Seguridad</p>
          <h4 className="dashboard-title mb-0">Gestion de roles y permisos</h4>
          <small className="text-muted">
            Define que puede hacer cada rol y controla sus accesos.
          </small>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                reloadRoles();
                reloadPermisos();
              }}
              disabled={loading || permisosLoading}
            >
              <i className="bi bi-arrow-repeat me-2" />
              Actualizar
            </button>
            {canCreateRoles && (
              <button
                type="button"
                className="btn management-btn-primary"
                onClick={handleNewRole}
              >
                + Nuevo rol
              </button>
            )}
          </div>
          <small className="text-muted">
            {roles.length} roles | {totalPermisos} permisos disponibles
          </small>
        </div>
      </div>

      {(listError || permisosError) && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center management-alert mb-4">
          <div>
            {listError && <p className="mb-1">Roles: {listError}</p>}
            {permisosError && <p className="mb-0">Permisos: {permisosError}</p>}
          </div>
          <div className="d-flex gap-2 flex-wrap">
            {listError && (
              <button className="btn btn-sm btn-outline-primary" onClick={reloadRoles}>
                Reintentar roles
              </button>
            )}
            {permisosError && (
              <button className="btn btn-sm btn-outline-secondary" onClick={reloadPermisos}>
                Reintentar permisos
              </button>
            )}
          </div>
        </div>
      )}

      <div className="management-card dashboard-table-card">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <p className="dashboard-label mb-1">Listado de roles</p>
            <h5 className="mb-0">Roles registrados</h5>
          </div>
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control roles-search-input"
              placeholder="Buscar por nombre o descripcion..."
              value={roleSearch}
              onChange={(e) => setRoleSearch(e.target.value)}
            />
          </div>
        </div>

        <RolesTable
          roles={roles}
          filteredRoles={filteredRoles}
          loading={loading}
          error={listError}
          onEdit={(rol) => handleOpenRole(rol.id)}
          canEditRoles={canEditRoles}
        />
      </div>

      {/* <ConfirmDeleteModal
        open={!isNuevo && confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          handleDelete();
          setShowModal(false);
        }}
        deleting={deleting}
        roleName={selectedRole?.nombre}
      /> */}
    </section>
  );
}

function RolesTable({ roles, filteredRoles, loading, error, onEdit, canEditRoles }) {
  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4">
        <p className="text-muted mb-2">{error}</p>
      </div>
    );
  }

  if (!roles.length) {
    return (
      <div className="dashboard-empty text-center py-5">
        <i className="bi bi-shield-lock dashboard-empty-icon mb-3"></i>
        <h5 className="fw-semibold mb-1">No hay roles registrados</h5>
        <p className="mb-0">Crea el primero para asignar permisos mas adelante.</p>
      </div>
    );
  }

  if (!filteredRoles.length) {
    return (
      <div className="text-center py-4">
        <p className="text-muted mb-0">No hay coincidencias con la busqueda.</p>
      </div>
    );
  }

  return (
    <div className="management-table-wrapper table-responsive">
      <table className="table align-middle management-table mb-0">
        <thead>
          <tr>
            <th>#</th>
            <th>Nombre</th>
            <th>Descripcion</th>
            <th>Tipo</th>
            <th className="text-center">Permisos</th>
            <th className="text-end">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredRoles.map((rol, index) => (
            <tr key={rol.id}>
              <td>{index + 1}</td>
              <td className="fw-semibold">{rol.nombre}</td>
              <td className="text-muted">{rol.descripcion || "Sin descripcion"}</td>
              <td>
                <span className="badge bg-light text-dark text-capitalize">{rol.tipo || "Rol"}</span>
              </td>
              <td className="text-center">
                <span className="badge bg-primary bg-opacity-10 text-primary">
                  <i className="bi bi-key me-1"></i>
                  {Array.isArray(rol.permisos) ? rol.permisos.length : 0}
                </span>
              </td>
              <td className="text-end">
                {canEditRoles ? (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => onEdit(rol)}
                  >
                    <i className="bi bi-pencil-square me-1"></i>
                    Editar
                  </button>
                ) : (
                  <span className="text-muted small">Sin permiso</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoleEditorPage({
  onBack,
  isNuevo,
  modules,
  activeModule,
  setActiveModule,
  formValues,
  setFormValues,
  selectedRole,
  selectedPermisos,
  visiblePermisos,
  permisosLoading,
  hasChanges,
  saving,
  // deleting,
  permSearch,
  setPermSearch,
  totalPermisos,
  visiblePermisosCount,
  onTogglePermiso,
  onToggleGroup,
  onSubmit,
  onReset,
  // onDelete,
  canManageRoles,
}) {
  const handleInputChange = (field) => (event) =>
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));

  const renderActiveModule = () => {
    if (permisosLoading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando permisos...</span>
          </div>
        </div>
      );
    }

    if (!modules.length) {
      return (
        <div className="dashboard-empty text-center py-4">
          <i className="bi bi-shield-check dashboard-empty-icon mb-3"></i>
          <p className="mb-1 fw-semibold">Sin permisos disponibles</p>
          <p className="text-muted mb-0">Recarga el catalogo o ajusta la busqueda.</p>
        </div>
      );
    }

    if (!activeModule) return null;

    const submodulos = visiblePermisos[activeModule] || {};
    return (
      <PermissionModule
        modulo={activeModule}
        submodulos={submodulos}
        selectedPermisos={selectedPermisos}
        onTogglePermiso={onTogglePermiso}
        onToggleGroup={onToggleGroup}
        disabled={!canManageRoles}
      />
    );
  };

  return (
    <>
      <div className="management-card management-header mb-4">
        <div>
          <p className="dashboard-eyebrow mb-1 text-uppercase">
            {isNuevo ? "Nuevo rol" : "Edicion de rol"}
          </p>
          <h4 className="dashboard-title mb-0">
            {isNuevo ? "Crear rol" : selectedRole?.nombre || "Rol"}
          </h4>
          <small className="text-muted">
            {isNuevo
              ? "Define nombre, tipo y permisos."
              : selectedRole?.descripcion || "Actualiza la configuracion del rol."}
          </small>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onBack}>
            <i className="bi bi-arrow-left me-2" />
            Volver
          </button>
          <button
            type="submit"
            form="role-editor-form"
            className="btn management-btn-primary btn-sm"
            disabled={saving || !hasChanges || !canManageRoles}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Guardando...
              </>
            ) : isNuevo ? (
              "Crear rol"
            ) : (
              "Guardar cambios"
            )}
          </button>
        </div>
      </div>

      <div className="management-card">
        <form id="role-editor-form" onSubmit={onSubmit}>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label dashboard-label">Nombre</label>
              <input
                type="text"
                className="form-control"
                value={formValues.nombre}
                onChange={handleInputChange("nombre")}
                required
                disabled={!canManageRoles}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label dashboard-label">Tipo</label>
              <select
                className="form-select"
                value={formValues.tipo}
                onChange={handleInputChange("tipo")}
                disabled={!canManageRoles}
              >
                {tipoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label dashboard-label">Descripcion</label>
              <textarea
                className="form-control"
                rows="2"
                value={formValues.descripcion}
                onChange={handleInputChange("descripcion")}
                required
                disabled={!canManageRoles}
              />
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
            <div>
              <p className="dashboard-label mb-1">Permisos del rol</p>
              <small className="text-muted">
                {selectedPermisos.length} de {visiblePermisosCount || totalPermisos} permisos marcados
              </small>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar permiso..."
                value={permSearch}
                onChange={(e) => setPermSearch(e.target.value)}
              />
              {hasChanges && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={onReset}
                  disabled={!canManageRoles}
                >
                  Restablecer
                </button>
              )}
            </div>
          </div>

          <div className="mb-3 overflow-auto">
            <div className="nav nav-pills gap-2 flex-nowrap">
              {modules.length ? (
                modules.map(({ modulo, total }) => (
                  <button
                    key={modulo}
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-2 ${activeModule === modulo ? "btn-primary" : "btn-outline-secondary"
                      }`}
                    onClick={() => setActiveModule(modulo)}
                  >
                    <span>{modulo}</span>
                    <span className="badge bg-light text-dark">{total}</span>
                  </button>
                ))
              ) : (
                <span className="text-muted small">Sin modulos</span>
              )}
            </div>
          </div>

          <div className="permission-grid">{renderActiveModule()}</div>

          <div className="d-flex justify-content-end gap-2 pt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={onBack}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn management-btn-primary"
              disabled={saving || !hasChanges || !canManageRoles}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Guardando...
                </>
              ) : isNuevo ? (
                "Crear rol"
              ) : (
                "Guardar cambios"
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function PermissionModule({
  modulo,
  submodulos,
  selectedPermisos,
  onTogglePermiso,
  onToggleGroup,
  disabled,
}) {
  const moduleCodes = Object.values(submodulos || {}).flatMap((lista) =>
    (lista || []).map((permiso) => permiso.codigo).filter(Boolean)
  );
  const allModuleActive =
    moduleCodes.length > 0 && moduleCodes.every((code) => selectedPermisos.includes(code));

  return (
    <div className="mb-3 p-3 border rounded-3 permission-module">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <span className="management-pill">{modulo}</span>
          <small className="text-muted">
            {moduleCodes.length} permiso{moduleCodes.length === 1 ? "" : "s"}
          </small>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => {
            if (disabled) return;
            onToggleGroup(moduleCodes, !allModuleActive);
          }}
          disabled={disabled}
        >
          {allModuleActive ? "Quitar modulo" : "Activar modulo"}
        </button>
      </div>

      {Object.entries(submodulos || {}).map(([submodulo, permisos]) => {
        const lista = permisos || [];
        const codes = lista.map((permiso) => permiso.codigo).filter(Boolean);
        const allActive = codes.length > 0 && codes.every((code) => selectedPermisos.includes(code));
        return (
          <div className="mb-2" key={`${modulo}-${submodulo}`}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="fw-semibold">{submodulo}</div>
              <button
                type="button"
                className="btn btn-link btn-sm text-decoration-none"
                onClick={() => {
                  if (disabled) return;
                  onToggleGroup(codes, !allActive);
                }}
                disabled={disabled}
              >
                {allActive ? "Quitar todo" : "Seleccionar todo"}
              </button>
            </div>
            <div className="row g-2">
              {lista.map((permiso) => (
                <div className="col-md-4 col-lg-3" key={permiso.codigo}>
                  <label className="d-flex gap-2 align-items-start p-2 border rounded-3 w-100">
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      checked={selectedPermisos.includes(permiso.codigo)}
                      onChange={() => onTogglePermiso(permiso.codigo)}
                      disabled={disabled}
                    />
                    <div>
                      <div className="fw-semibold">{permiso.descripcion || permiso.codigo}</div>
                      {/* <small className="text-muted">{permiso.codigo}</small> */}
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

